import React, { useCallback, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';

// Styles & Assets
import styles from './PatientPage.module.css';

// Services
import { getEnvironmentApiUrl } from '../../services/General';

// Context
import ProfileContext from '../../context/ProfileContext';
import GeneralContext from '../../context/GeneralContext';

// Hooks
import { useState } from 'react';
import useHttp from '../../hooks/useHttp';

// Components
import Toolbar from '../../components/UI/Toolbar/Toolbar';
import PatientInfo from '../../components/Patient/PatientInfo/PatientInfo';
import PatientContentButtons from '../../components/Patient/PatientContentButtons/PatientContentButtons';
import ErrorSpinnerWrapper from '../../components/UI/ErrorSpinnerWrapper.tsx/ErrorSpinnerWrapper';
import PatientDischargeButton from '../../components/Patient/PatientDischargeButton/PatientDischargeButton';
import SelectEpisodeButton from '../../components/Patient/SelectEpisodeButton/SelectEpisodeButton';
import PatientGenerateQRButton from '../../components/Patient/PatientGenerateQRButton/PatientGenerateQRButton';
import LockButton from '../../components/Patient/LockButton/LockButton';

// Language
import { useTranslation } from 'react-i18next';

// Interfaces
import { Case, IScoreCircle, Patient } from '../../interfaces/Patient';

// Other
import { useParams } from 'react-router-dom';
import { canEditCaseView } from '../../services/user';
import usePatientStore from '../../stores/patient-store';
import ChatPanel from '../../components/Patient/ChatPanel/ChatPanel';

const PatientPage: React.FC = () => {
    const { t } = useTranslation();
    const { id } = useParams();

    const navigate = useNavigate();

    const caseID = Number(id);

    const { profileAttributes } = useContext(ProfileContext);
    const generalContext = useContext(GeneralContext);

    const { isLoading: loadingCase, error: errorCase, sendRequest: getCase } = useHttp<Case>();
    const { isLoading: loadingPatient, error: errorPatient, sendRequest: getPatient } = useHttp<Patient>();

    const setPatient = usePatientStore(state => state.setPatient);
    const setCase = usePatientStore(state => state.setCase);
    const caso = usePatientStore(state => state.case);
    const updateCase = usePatientStore(state => state.updateCase);

    const [scoreCV, setScoreCV] = useState<IScoreCircle>({
        lastScore: undefined,
        secondLastScore: undefined
    });

    const updateScore = useCallback((cv: IScoreCircle) => setScoreCV(cv), []);

    const loadCaseAndPatient = useCallback((targetCaseID: number) => {
        getCase(
            {
                url: getEnvironmentApiUrl() + `case/${targetCaseID}/extended`,
                method: 'GET',
            },
            (caseData) => {
                setCase(caseData);

                getPatient(
                    {
                        url: getEnvironmentApiUrl() + `patient/${caseData.patient_id}/extended`,
                        method: 'GET',
                    },
                    (patientData) => {
                        setPatient(patientData);
                    }
                );
            }
        );
    }, [getCase, getPatient, setCase, setPatient]);

    const refreshPatientSilently = useCallback(() => {
        return new Promise<void>((resolve, reject) => {
            if (!caseID) {
                resolve();
                return;
            }

            getCase(
                {
                    url: getEnvironmentApiUrl() + `case/${caseID}/extended`,
                    method: 'GET',
                },
                (caseData) => {
                    setCase(caseData);

                    getPatient(
                        {
                            url: getEnvironmentApiUrl() + `patient/${caseData.patient_id}/extended`,
                            method: 'GET',
                        },
                        (patientData) => {
                            setPatient(patientData);
                            resolve();
                        },
                        (patientError) => {
                            reject(patientError);
                        }
                    );
                },
                (caseError) => {
                    reject(caseError);
                }
            );
        });
    }, [caseID, getCase, getPatient, setCase, setPatient]);

    const closedCase = typeof caso?.end_date === 'string';
    const readOnly = !canEditCaseView(caso);

    const openCloseCaseEnabled = window.config.IS_OPEN_CLOSE_CASE_ENABLED;

    useEffect(() => {
        if (!id) return;
        loadCaseAndPatient(Number(id));
    }, [id, loadCaseAndPatient]);

    const showPatientCharts = profileAttributes?.showPatientCharts;

    useEffect(() => {
        if (showPatientCharts !== false) return;
        navigate('patients_data_table');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showPatientCharts]);

    return (
        <div className='pageContainer'>
            <Toolbar text={t('patients')} link='/home/patients'>
                <div className={styles.buttonContainer}>
                    <SelectEpisodeButton caseID={caseID} />
                    {
                        openCloseCaseEnabled && (
                            <PatientDischargeButton
                                caso={caso}
                                disabled={readOnly || closedCase}
                                updateCase={updateCase}
                            />
                        )
                    }
                    <PatientGenerateQRButton
                        readOnly={readOnly}
                    />
                </div>
            </Toolbar>

            {/* Main content with toolbar already inside from parent */}
            <div className={styles.pageContent}>
                {/* Content area with two columns */}
                <div className={styles.contentArea}>
                    {/* Left column - 70% */}
                    <div className={styles.leftColumn}>
                        <ErrorSpinnerWrapper loading={loadingCase || loadingPatient} error={errorCase || errorPatient}>
                            {caso && (
                                <>
                                    <PatientInfo
                                        closedCase={closedCase}
                                        caso={caso}
                                        scoreCV={scoreCV}
                                        readOnly={readOnly}
                                        refreshPatientSilently={refreshPatientSilently}
                                    />

                                    <div className={`${styles.contentContainer} ${closedCase ? styles.closedBkg : ''}`}>
                                        <PatientContentButtons
                                            caso={caso}
                                            updateScore={updateScore}
                                            refreshPatientSilently={refreshPatientSilently}
                                        />
                                        {closedCase && (
                                            <LockButton
                                                caso={caso}
                                                disabled={readOnly || !openCloseCaseEnabled}
                                                updateCase={updateCase}
                                            />
                                        )}
                                    </div>
                                </>
                            )}
                        </ErrorSpinnerWrapper>
                    </div>
                    
                    {/* Right column - 30% for chat */}
                    {generalContext?.isChatOpen && (
                        <div className={styles.chatWrapper}>
                            <ChatPanel handleClickClose={() => generalContext?.setIsChatOpen?.(false)} />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );

};
export default PatientPage;